import { IFormControl } from './formcontrol.interface';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { IFormFieldOverrideConfig, IFormFieldOverrides } from './form-field-override-config.interface';
import { IFormValidator } from './form-validator.interface';
import { FormValidatorType } from './form-validator-type';

export interface IFormFieldOverrideContext {
  country: string;
  deliveryMethod?: DeliveryMethodType;
}

/** Applies config-driven, one-to-one patches to backend form fields. Pure; no DI. */
export class FormFieldOverrideTransformer {

  /** Patches fields matching `ctx` + fieldId. Returns input unchanged when nothing matches. */
  static applyOverrides(
    fields: IFormControl[],
    ctx: IFormFieldOverrideContext,
    configs: IFormFieldOverrideConfig[],
  ): IFormControl[] {
    const matched = configs.filter(
      c => c.country === ctx.country && c.deliveryMethod === ctx.deliveryMethod,
    );
    if (matched.length === 0) return fields;

    return fields.map(field => {
      const override = matched.find(c => c.fieldId === field.id);
      return override ? FormFieldOverrideTransformer.applyTo(field, override.overrides) : field;
    });
  }

  /** Shallow-clones `field` and applies whitelisted overrides (undefined = leave alone). */
  private static applyTo(field: IFormControl, o: IFormFieldOverrides): IFormControl {
    const next: IFormControl = { ...field };

    if (o.label !== undefined)       next.label       = o.label;
    if (o.hint !== undefined)        next.hint        = o.hint;
    if (o.placeholder !== undefined) next.placeholder = o.placeholder;
    if (o.type !== undefined)        next.type        = o.type;
    if (o.options !== undefined)     next.options     = o.options;
    if (o.disabled !== undefined)    next.disabled    = o.disabled;
    if (o.value !== undefined)       next.value       = o.value;

    if (o.validators || o.pattern !== undefined) {
      next.validators = FormFieldOverrideTransformer.patchValidators(
        field.validators || [],
        o.validators,
        o.pattern,
      );
      if (o.pattern !== undefined) next.pattern = o.pattern;
    }

    return next;
  }

  /** Patches each validator by FormValidatorType; unknown types ignored. `pattern` shorthand patches Regex, explicit wins. */
  private static patchValidators(
    existing: IFormValidator[],
    patches: Partial<Record<FormValidatorType, Partial<IFormValidator>>> | undefined,
    patternShorthand: string | undefined,
  ): IFormValidator[] {
    const effective: Partial<Record<FormValidatorType, Partial<IFormValidator>>> = { ...(patches || {}) };

    if (patternShorthand !== undefined) {
      const explicit = effective[FormValidatorType.Regex];
      const hasExplicitRegex = explicit && explicit.regex !== undefined;
      if (!hasExplicitRegex) {
        effective[FormValidatorType.Regex] = {
          ...(explicit || {}),
          regex: new RegExp(patternShorthand),
        };
      } else {
        console.warn(
          '[FormFieldOverride] both `pattern` and `validators.Regex.regex` set; using `validators.Regex.regex`',
        );
      }
    }

    return existing.map(v => {
      const patch = effective[v.type];
      return patch ? { ...v, ...patch } : v;
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/formbuilder/form-field-override-transformer.ts